import { ReactNode } from "react";

/** Labels/help/titles can be ReactNode; placeholders remain strings */
type LabelNode = ReactNode;

export interface IContractorCopy {
  appTitle: LabelNode;
  sectionHelpHeader: LabelNode;

  buttons: {
    previous: LabelNode;
    next: LabelNode;
    submit: LabelNode;
    wizardView: LabelNode;
    verticalView: LabelNode;
    demoToggle: LabelNode;
    completeBadge: LabelNode;
  };

  personal: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      contactFirst: { label: LabelNode; placeholder: string };
      contactLast: { label: LabelNode; placeholder: string };
      email: { label: LabelNode; placeholder: string };
      phone: { label: LabelNode; placeholder: string };
    };
  };

  business: {
    title: LabelNode;
    help: LabelNode;
    feinTip: LabelNode;
    feinInvalid: LabelNode;
    feinLengthPrefix: LabelNode;
    fields: {
      businessName: { label: LabelNode; placeholder: string };
      fein: { label: LabelNode; placeholder: string };
      yearsInBusiness: { label: LabelNode; placeholder: string };
      employeeCount: { label: LabelNode; placeholder: string };
      tradeType: { label: LabelNode; placeholder: string };
    };
  };

  requirements: {
    title: LabelNode;
    help: LabelNode;
    needReqCheckbox: LabelNode;
    fields: {
      additionalInsured: {
        label: LabelNode;
        placeholder: string;
        options: { none: LabelNode; perProject: LabelNode; blanket: LabelNode };
      };
      waiverOfSubrogation: {
        label: LabelNode;
        placeholder: string;
        options: { yes: LabelNode; no: LabelNode };
      };
      limitsPerOccurrence: { label: LabelNode; placeholder: string };
      limitsAggregate: { label: LabelNode; placeholder: string };
      certHolders: { label: LabelNode; placeholder: string };
    };
  };

  vehicles: {
    title: LabelNode;
    help: LabelNode;
    ownFleetCheckbox: LabelNode;
    fields: {
      vehicleCount: { label: LabelNode; placeholder: string };
      primaryVehicleType: {
        label: LabelNode;
        placeholder: string;
        options: { pickups: LabelNode; vans: LabelNode; boxTrucks: LabelNode; trailers: LabelNode };
      };
      radiusMiles: { label: LabelNode; placeholder: string };
    };
  };

  coverage: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      coverageType: {
        label: LabelNode;
        placeholder: string;
        options: { glOnly: LabelNode; glAndAuto: LabelNode; fullPackage: LabelNode; custom: LabelNode };
      };
      glDeductible: { label: LabelNode; placeholder: string };
    };
  };

  remarks: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      remarks: { label: LabelNode; placeholder: string };
    };
  };

  submitThankYou: {
    header: LabelNode;
    body: LabelNode;
  };
}

export const CONTRACTOR_COPY: IContractorCopy = {
  appTitle: <>🏗 Contractor Insurance Quote</>,
  sectionHelpHeader: <>Section Help</>,

  buttons: {
    previous: <>Previous</>,
    next: <>Next</>,
    submit: (
      <>
        🚀 <strong>Submit</strong>
      </>
    ),
    wizardView: <>Wizard View</>,
    verticalView: <>Vertical View</>,
    demoToggle: <>Demo Mode (auto-fill sample data)</>,
    completeBadge: <>Complete</>,
  },

  personal: {
    title: <>Contact Person</>,
    help: <>Who should we reach out to about this quote?</>,
    fields: {
      contactFirst: { label: <>First Name</>, placeholder: "Alex" },
      contactLast: { label: <>Last Name</>, placeholder: "Builder" },
      email: { label: <>Email</>, placeholder: "alex.builder@example.com" },
      phone: { label: <>Phone</>, placeholder: "(555) 555-0123" },
    },
  },

  business: {
    title: <>Business Information</>,
    help: (
      <>
        Tell us about your contracting business so we can find the right coverage options.
      </>
    ),
    feinTip: (
      <>
        Tip: FEIN is a 9-digit number (digits only). For sole proprietors without FEIN, you can
        enter SSN but please share SSN securely with your agent, not here.
      </>
    ),
    feinInvalid: <>FEIN must be 9 digits.</>,
    feinLengthPrefix: <>FEIN length</>,
    fields: {
      businessName: { label: <>Business Name</>, placeholder: "Acme Contracting LLC" },
      fein: { label: <>FEIN (digits only)</>, placeholder: "#########" },
      yearsInBusiness: { label: <>Years in Business</>, placeholder: "e.g. 7" },
      employeeCount: { label: <>Employee Count</>, placeholder: "e.g. 10" },
      tradeType: { label: <>Trade Type</>, placeholder: "e.g. Electrical, Painting, HVAC" },
    },
  },

  requirements: {
    title: <>Project / Client Requirements</>,
    help: (
      <>
        Some clients require additional insured endorsements, waivers of subrogation, or specific
        limits. Enable if this applies to you.
      </>
    ),
    needReqCheckbox: <>I have client/project insurance requirements</>,
    fields: {
      additionalInsured: {
        label: <>Additional Insured</>,
        placeholder: "Select…",
        options: {
          none: <>None</>,
          perProject: <>Per-Project</>,
          blanket: <>Blanket</>,
        },
      },
      waiverOfSubrogation: {
        label: <>Waiver of Subrogation</>,
        placeholder: "Select…",
        options: { yes: <>Yes</>, no: <>No</> },
      },
      limitsPerOccurrence: { label: <>GL Limit — Per Occurrence</>, placeholder: "e.g. 1,000,000" },
      limitsAggregate: { label: <>GL Limit — Aggregate</>, placeholder: "e.g. 2,000,000" },
      certHolders: { label: <>Certificate Holders (comma-separated)</>, placeholder: "Owner A, GC B" },
    },
  },

  vehicles: {
    title: <>Vehicles (Optional)</>,
    help: <>If you have company vehicles, add basic details here.</>,
    ownFleetCheckbox: <>We have company vehicles</>,
    fields: {
      vehicleCount: { label: <>Number of Vehicles</>, placeholder: "e.g. 3" },
      primaryVehicleType: {
        label: <>Primary Vehicle Type</>,
        placeholder: "Select…",
        options: {
          pickups: <>Pickups</>,
          vans: <>Cargo Vans</>,
          boxTrucks: <>Box Trucks</>,
          trailers: <>Trailers</>,
        },
      },
      radiusMiles: { label: <>Radius of Operation (miles)</>, placeholder: "e.g. 50" },
    },
  },

  coverage: {
    title: <>Coverage (Optional)</>,
    help: <>Pick a starting point or leave blank to discuss with an agent.</>,
    fields: {
      coverageType: {
        label: <>Coverage Package</>,
        placeholder: "Select…",
        options: {
          glOnly: <>General Liability Only</>,
          glAndAuto: <>GL + Commercial Auto</>,
          fullPackage: <>GL + Auto + Tools/Equipment</>,
          custom: <>Custom</>,
        },
      },
      glDeductible: { label: <>GL Deductible</>, placeholder: "e.g. 500" },
    },
  },

  remarks: {
    title: <>Remarks (Optional)</>,
    help: <>Anything else we should know?</>,
    fields: {
      remarks: { label: <>Remarks</>, placeholder: "Job types, typical contract values, etc." },
    },
  },

  submitThankYou: {
    header: <>Thank you!</>,
    body: (
      <>
        An insurance agent will reach out to you to review and complete your application.
      </>
    ),
  },
};
